import React, { useCallback, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import Typography from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/typography';

export interface Option {
  name: string;
  value: string | number;
}
export interface Props {
  label?: string;
  required?: boolean;
  labelClassName?: string;
  options?: Option[];
  defaultValue?: Option;
  onChange?: (option: Option) => void;
  /** Replaces `atom-select-button` on the trigger; options become `<data-testid>-option-<index>`. */
  'data-testid'?: string;
}

const Select: React.FC<Props> = ({
  onChange,
  defaultValue,
  label,
  required,
  labelClassName = '',
  options = [],
  'data-testid': dataTestId,
}) => {
  const [selected, setSelected] = useState<Option | undefined>(defaultValue ?? options?.[0]);

  useEffect(() => {
    setSelected(defaultValue ?? options?.[0]);
  }, [defaultValue, options]);

  const handleChange = useCallback(
    (option: Option) => {
      setSelected(option);
      onChange?.(option);
    },
    [onChange],
  );

  return (
    <>
      {label && (
        <div className="mb-8">
          <Typography fontSize={14} as="label" medium className={`${labelClassName} text-secondary-regular`}>
            {required ? `${label} *` : label}
          </Typography>
        </div>
      )}
      <Listbox value={selected} onChange={handleChange} data-testid="atom-select">
        <div className="relative">
          <Listbox.Button
            data-testid={dataTestId ?? 'atom-select-button'}
            className="relative flex h-40 w-full cursor-default items-center rounded-sm border border-neutral-500 bg-site pl-8 pr-32 text-left focus:outline-none">
            <span className="text-14">{selected?.name}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-8">
              <ChevronDownIcon className="size-20 text-secondary-regular" aria-hidden="true" />
            </span>
          </Listbox.Button>
          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-[999] mt-1 max-h-200 w-full min-w-200 overflow-auto rounded-sm bg-site shadow-lg focus:outline-none">
              {options.map((option, index) => (
                <Listbox.Option
                  key={option.value}
                  data-testid={`${dataTestId ?? 'atom-select'}-option-${index}`}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-8 pr-4 text-14 hover:bg-neutral-200 ${
                      active ? 'bg-neutral-200' : ''
                    }`
                  }
                  value={option}
                >
                  {({ selected }) => (
                    <>
                      <span className={`block truncate text-14 ${selected ? 'font-medium' : 'font-normal'}`}>
                        {option.name}
                      </span>
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                          <CheckIcon className="size-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </>
  );
};

export default Select;
